import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { ArrowLeft, Save, Loader2, AlertTriangle, CheckCircle2, RefreshCw } from "lucide-react";
import UrlPreview from "../components/UrlPreview";
import { toast } from "sonner";

const CTA_DEFAULT = "LEARN_MORE";

function MetaAdPreview({ adId }) {
  const [formats, setFormats] = useState([]);
  const [formatKey, setFormatKey] = useState("MOBILE_FEED_STANDARD");
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/meta/ad-edit/preview-formats")
      .then((r) => setFormats(r.data.formats || []))
      .catch(() => setFormats([]));
  }, []);

  const load = async () => {
    if (!adId) return;
    setLoading(true); setErr(""); setHtml("");
    try {
      const r = await api.get("/meta/ad-edit/preview", {
        params: { ad_id: adId, format: formatKey },
      });
      if (r.data.iframe_html) setHtml(r.data.iframe_html);
      else setErr(r.data.error || "Meta returned no preview for this format.");
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [adId, formatKey]);

  if (!adId) return null;
  return (
    <div className="bg-white border border-zinc-200 p-5" data-testid="meta-native-preview">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div>
          <div className="data-label">Native Meta preview</div>
          <div className="text-[11px] text-zinc-500 font-mono mt-1">Rendered by Meta — reflects the CURRENT live ad ({adId})</div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={formatKey} onValueChange={setFormatKey}>
            <SelectTrigger className="rounded-none border-zinc-300 w-52" data-testid="preview-format-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {formats.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <button onClick={load} disabled={loading} data-testid="preview-refresh-btn"
            className="p-2 border border-zinc-300 hover:bg-zinc-50">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>
      <div className="border border-zinc-200 bg-zinc-100 min-h-[600px] flex items-center justify-center overflow-auto">
        {loading && <span className="text-xs font-mono text-zinc-500"><Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Loading…</span>}
        {!loading && err && <span className="text-xs font-mono text-red-700 px-4">{err}</span>}
        {!loading && !err && html && (
          <div className="bg-white" dangerouslySetInnerHTML={{ __html: html }} />
        )}
      </div>
    </div>
  );
}

export default function EditMetaAd() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const accountId = searchParams.get("account_id") || "";
  const adName = searchParams.get("name") || "";

  const [loading, setLoading] = useState(false);
  const [placements, setPlacements] = useState([]);
  const [template, setTemplate] = useState(null);
  const [ctaTypes, setCtaTypes] = useState([CTA_DEFAULT]);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // editable fields
  const [newAdName, setNewAdName] = useState("");
  const [primaryText, setPrimaryText] = useState("");
  const [headline, setHeadline] = useState("");
  const [linkDescription, setLinkDescription] = useState("");
  const [destinationUrl, setDestinationUrl] = useState("");
  const [ctaType, setCtaType] = useState(CTA_DEFAULT);
  const [addUtm, setAddUtm] = useState(true);
  const [addTm, setAddTm] = useState(true);

  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  // ----- load placements -----
  useEffect(() => {
    if (!accountId || !adName) return;
    setLoading(true);
    api.get("/meta/ad-edit/by-name", { params: { account_id: accountId, ad_name: adName } })
      .then((r) => {
        setPlacements(r.data.placements || []);
        setTemplate(r.data.template || null);
        const t = r.data.template;
        if (t) {
          setNewAdName(t.ad_name || "");
          setPrimaryText(t.primary_text || "");
          setHeadline(t.headline || "");
          setLinkDescription(t.link_description || "");
          setDestinationUrl(t.destination_url || "");
          setCtaType(t.cta_type || CTA_DEFAULT);
        }
        // select all by default
        setSelectedIds(new Set((r.data.placements || []).map((p) => p.ad_id)));
      })
      .catch((e) => toast.error("Failed to load ad", { description: e.response?.data?.detail || e.message }))
      .finally(() => setLoading(false));
    api.get("/meta/ad-creation/cta-types").then((r) => setCtaTypes(r.data.cta_types || [CTA_DEFAULT])).catch(() => {});
  }, [accountId, adName]);

  const toggleId = (id) => {
    setSelectedIds((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const creativeKind = useMemo(() => {
    if (!template) return null;
    if (template.video_id) return "video";
    if (template.image_hash) return "image";
    if (template.creative_object_type === "SHARE" && template.effective_object_story_id) return "existing_post";
    return template.creative_object_type || "unknown";
  }, [template]);

  const isShareOnly = creativeKind === "existing_post" || creativeKind === "SHARE";

  // diff against template — only send changed fields
  const buildPayload = () => {
    const ad_ids = Array.from(selectedIds);
    const p = { account_id: accountId, ad_ids, add_utm: addUtm, add_tm_params: addTm };
    if (template && newAdName.trim() && newAdName.trim() !== (template.ad_name || "")) {
      p.new_ad_name = newAdName.trim();
    }
    if (!isShareOnly) {
      if (primaryText !== (template?.primary_text || "")) p.primary_text = primaryText;
      if (headline !== (template?.headline || "")) p.headline = headline;
      if (linkDescription !== (template?.link_description || "")) p.link_description = linkDescription;
      if (destinationUrl !== (template?.destination_url || "")) p.destination_url = destinationUrl;
      if (ctaType !== (template?.cta_type || CTA_DEFAULT)) p.cta_type = ctaType;
    }
    return p;
  };

  const apply = async () => {
    const payload = buildPayload();
    const editedKeys = Object.keys(payload).filter((k) => !["account_id", "ad_ids", "add_utm", "add_tm_params"].includes(k));
    if (editedKeys.length === 0) {
      toast.error("Nothing to update — change at least one field");
      return;
    }
    if (payload.ad_ids.length === 0) {
      toast.error("Select at least one placement");
      return;
    }
    const verb = editedKeys.length === 1 && editedKeys[0] === "new_ad_name" ? "Rename" : "Update";
    if (!window.confirm(`${verb} ${payload.ad_ids.length} ad placement(s)? Changed: ${editedKeys.join(", ")}`)) return;
    setBusy(true); setResult(null);
    try {
      const r = await api.post("/meta/ad-edit/update-bulk", payload);
      setResult(r.data);
      if (r.data.failure > 0) {
        toast.error(`${verb}d ${r.data.success} ok / ${r.data.failure} failed`);
      } else {
        toast.success(`${verb}d ${r.data.success} placement${r.data.success === 1 ? "" : "s"}`);
      }
    } catch (e) {
      toast.error("Update failed", { description: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="edit-meta-ad-page" className="fade-in">
      <PageHeader
        eyebrow="Meta Ads"
        title="Edit ad copy"
        description="Update the ad name and copy fields once — changes are propagated to every selected placement (a new creative is swapped in per placement)."
        actions={
          <Button variant="outline" onClick={() => navigate("/meta-ads")} data-testid="edit-back-btn"
            className="rounded-none border-zinc-300 font-mono uppercase tracking-wider text-xs">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Meta Ads
          </Button>
        }
      />

      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8 space-y-6">
        {loading && <div className="font-mono text-sm text-zinc-500">Loading ad data…</div>}
        {!loading && !template && (
          <div className="p-4 border border-amber-300 bg-amber-50 text-sm">
            No ad named <code className="font-mono">{adName}</code> found on this account.
          </div>
        )}
        {!loading && template && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Form */}
            <div className="lg:col-span-2 space-y-5">
              <div className="bg-white border border-zinc-200 p-5 space-y-4" data-testid="edit-form">
                <div className="flex items-center justify-between">
                  <div className="data-label">Edit fields</div>
                  <span className="text-[10px] font-mono uppercase text-zinc-500">
                    Creative type: <span className="text-zinc-900">{creativeKind}</span>
                  </span>
                </div>
                <div>
                  <Label className="data-label">Ad name</Label>
                  <Input value={newAdName} onChange={(e) => setNewAdName(e.target.value)} data-testid="edit-ad-name"
                    className="rounded-none border-zinc-300 mt-1 font-mono text-sm" />
                </div>
                {isShareOnly && (
                  <div className="p-3 border border-amber-300 bg-amber-50 text-xs font-mono flex gap-2"
                       data-testid="share-only-warning">
                    <AlertTriangle className="w-4 h-4 shrink-0 text-amber-700" />
                    <div>
                      This ad uses an existing Page post (creative type <span className="text-zinc-900">{creativeKind}</span>).
                      Primary text / headline / link description live on the underlying Facebook post and cannot
                      be edited through the Ads API. You can still rename the ad here.
                    </div>
                  </div>
                )}
                {!isShareOnly && (
                  <>
                    <div>
                      <Label className="data-label">Primary text</Label>
                      <Textarea value={primaryText} onChange={(e) => setPrimaryText(e.target.value)} data-testid="edit-primary-text"
                        className="rounded-none border-zinc-300 mt-1 font-mono text-sm" rows={3} />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="data-label">Headline</Label>
                        <Input value={headline} onChange={(e) => setHeadline(e.target.value)} data-testid="edit-headline"
                          className="rounded-none border-zinc-300 mt-1 font-mono text-sm" />
                      </div>
                      <div>
                        <Label className="data-label">Link description</Label>
                        <Input value={linkDescription} onChange={(e) => setLinkDescription(e.target.value)} data-testid="edit-link-description"
                          className="rounded-none border-zinc-300 mt-1 font-mono text-sm" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="data-label">CTA button</Label>
                        <Select value={ctaType} onValueChange={setCtaType}>
                          <SelectTrigger className="rounded-none border-zinc-300 mt-1" data-testid="edit-cta">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ctaTypes.map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="data-label">Destination URL</Label>
                        <Input value={destinationUrl} onChange={(e) => setDestinationUrl(e.target.value)} data-testid="edit-destination-url"
                          className="rounded-none border-zinc-300 mt-1 font-mono text-sm" />
                      </div>
                    </div>
                    <UrlPreview baseUrl={destinationUrl} addTm={addTm} addUtm={addUtm} />
                    <div className="flex gap-6 pt-2">
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox checked={addTm} onCheckedChange={(v) => setAddTm(!!v)} data-testid="edit-add-tm" />
                        <span>Re-append <code className="font-mono text-xs">tmcid</code>/<code className="font-mono text-xs">tmsid</code></span>
                      </label>
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox checked={addUtm} onCheckedChange={(v) => setAddUtm(!!v)} data-testid="edit-add-utm" />
                        <span>Re-append <code className="font-mono text-xs">utm_campaign</code>/<code className="font-mono text-xs">utm_term</code></span>
                      </label>
                    </div>
                  </>
                )}
                <div className="pt-3 border-t border-zinc-200">
                  <Button onClick={apply} disabled={busy || selectedIds.size === 0} data-testid="edit-apply-btn"
                    className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-mono uppercase tracking-wider text-xs">
                    {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating…</> : <><Save className="w-4 h-4 mr-2" /> Update {selectedIds.size} placement{selectedIds.size === 1 ? "" : "s"}</>}
                  </Button>
                </div>
              </div>

              {result && (
                <div className={`p-4 border ${result.failure > 0 ? "border-red-300 bg-red-50" : "border-emerald-300 bg-emerald-50"}`}
                     data-testid="edit-result">
                  <div className="font-mono text-sm flex items-center gap-2">
                    {result.failure > 0
                      ? <AlertTriangle className="w-4 h-4 text-red-700" />
                      : <CheckCircle2 className="w-4 h-4 text-emerald-700" />}
                    {result.success} updated · {result.failure} failed
                  </div>
                  <ul className="text-xs font-mono mt-2 space-y-1 max-h-60 overflow-auto">
                    {result.results.map((r, i) => (
                      <li key={i} className={r.ok ? "text-emerald-800" : "text-red-700"}>
                        {r.ok ? "✓" : "✗"} {r.ad_id} {r.ok && r.new_creative_id ? `→ new creative ${r.new_creative_id}` : r.error ? `→ ${r.error}` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Live (Meta-rendered) ad preview */}
              <MetaAdPreview adId={placements[0]?.ad_id} />

              {/* Preview card */}
              <div className="bg-white border border-zinc-200 p-5">
                <div className="data-label mb-3">Static preview (your draft copy)</div>
                <div className="border border-zinc-200 max-w-md">
                  <div className="px-3 py-2 border-b border-zinc-200 text-xs font-mono text-zinc-600">
                    {template.page_id ? `Page ${template.page_id}` : "—"}
                  </div>
                  <div className="px-3 py-2 text-sm whitespace-pre-wrap" data-testid="preview-primary-text">
                    {isShareOnly ? <span className="italic text-zinc-500">— Lives on the underlying post —</span> : (primaryText || <span className="text-zinc-300">Primary text…</span>)}
                  </div>
                  <div className="aspect-square bg-zinc-100 flex items-center justify-center text-zinc-400 text-xs font-mono">
                    {template.image_hash ? `Image ${template.image_hash.slice(0,8)}…` :
                     template.video_id ? `Video ${template.video_id}` :
                     template.effective_object_story_id ? `Post ${template.effective_object_story_id}` : "No creative preview"}
                  </div>
                  <div className="px-3 py-2 border-t border-zinc-200">
                    <div className="text-[10px] uppercase font-mono text-zinc-500">{(destinationUrl || "").replace(/^https?:\/\//, "").split("/")[0] || "—"}</div>
                    <div className="text-sm font-semibold" data-testid="preview-headline">
                      {isShareOnly ? "—" : (headline || <span className="text-zinc-300">Headline…</span>)}
                    </div>
                    <div className="text-xs text-zinc-600 mt-0.5" data-testid="preview-link-description">
                      {isShareOnly ? "—" : (linkDescription || <span className="text-zinc-300">Description…</span>)}
                    </div>
                    {!isShareOnly && (
                      <div className="mt-2 inline-block px-3 py-1 bg-zinc-100 text-xs font-semibold font-mono uppercase tracking-wider">
                        {ctaType.replace(/_/g, " ")}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Placements panel */}
            <div className="bg-white border border-zinc-200 p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div className="data-label">Placements</div>
                <span className="text-xs font-mono text-zinc-500">{selectedIds.size}/{placements.length}</span>
              </div>
              <div className="flex items-center gap-3 text-xs font-mono">
                <button onClick={() => setSelectedIds(new Set(placements.map((p) => p.ad_id)))} data-testid="edit-select-all-placements"
                  className="hover:underline uppercase">all</button>
                <span className="text-zinc-300">·</span>
                <button onClick={() => setSelectedIds(new Set())} data-testid="edit-clear-placements"
                  className="hover:underline uppercase">none</button>
              </div>
              <ul className="max-h-[520px] overflow-auto border border-zinc-200">
                {placements.map((p) => {
                  const sel = selectedIds.has(p.ad_id);
                  return (
                    <li key={p.ad_id} className="border-b border-zinc-100 last:border-0">
                      <label className="flex items-start gap-2 px-2 py-2 hover:bg-zinc-50 cursor-pointer">
                        <Checkbox checked={sel} onCheckedChange={() => toggleId(p.ad_id)}
                          data-testid={`edit-placement-${p.ad_id}`} className="mt-1" />
                        <div className="min-w-0 flex-1">
                          <div className="text-xs truncate" title={p.adset_name}>{p.adset_name || "—"}</div>
                          <div className="text-[10px] font-mono text-zinc-500 truncate">
                            {p.campaign_name || "—"} · {p.effective_status || p.status}
                          </div>
                          <div className="text-[10px] font-mono text-zinc-400">ad {p.ad_id}</div>
                        </div>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
